import { FC, useRef } from 'react';
import { Form, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useAssistant } from '@/entities/assistant/hooks/useAssistant';
import { getPersonalIdentifierForms } from '@/entities/personalIdentifiers/helpers/get-forms';
import { useI18nValues } from '@/entities/personalIdentifiers/hooks/useI18nValues';
import { useFormRenderV2 } from '@/shared/lib/hooks/useFormRenderV2';
import { FileUploadingRef } from '@/shared/lib/hooks/useFormRenderV2/components/FileUploading';
import { PersonalIdentifierDocumentDto, PersonalIdentifierType } from '@/shared/lib/sj-orm';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { Button } from '@/shared/uikitV2/Button';

import { BtnClose } from '../../components/BtnClose';
import { SelectOptions } from '../../components/SelectItem/types';

import { Body, Container, Footer, Header, StyledDrawer, StyledTitle } from './styled';

type CreatePersonalIdProps = {
  personalIdType?: PersonalIdentifierType;
  onCreate?: (data: SelectOptions) => void;
  setOpen: (open: boolean) => void;
};

export const CreatePersonalId: FC<CreatePersonalIdProps> = ({
  personalIdType = PersonalIdentifierType.FREE_BLOCK,
  onCreate,
  setOpen,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectEntities' });
  const { keyPair } = useKeyPairStore();
  const sjDb = useSJDatabase();
  const values = useI18nValues();
  const uploaderRef = useRef<FileUploadingRef>(null);
  const { delegation } = useAssistant();
  const { profileType, isAssistant } = useProfileStore();

  const form = useForm<Omit<PersonalIdentifierDocumentDto, 'createdAt' | 'updatedAt'>>({
    defaultValues: {},
    mode: 'onChange',
  });

  const { Component } = useFormRenderV2({
    // @ts-ignore
    sectionItems: getPersonalIdentifierForms(personalIdType)[0],
    // @ts-ignore
    form,
    uploaderRef,
    showRequiredOnly: true,
    onHideFieldPredicate: (condition) => {
      if (!profileType || !condition.allowedProfileTypes) {
        return false;
      }
      return condition.allowedProfileTypes.includes(profileType);
    },
  });

  const onSubmit = async (data: PersonalIdentifierDocumentDto) => {
    if (!keyPair) return;
    const res = uploaderRef.current ? await uploaderRef.current.upload(true) : null;

    const personalIdDto: PersonalIdentifierDocumentDto = {
      ...data,
      id: sjDb.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS),
      type: DocumentType.PERSONAL_IDENTIFIERS,
      personalIdentifierType: personalIdType,
    };

    if (res?.success) {
      personalIdDto.scan = res.filePaths[0];
    }

    const publicKeys = [keyPair.publicKey];

    if (delegation?.ownerPublicKey && isAssistant) {
      publicKeys.push(delegation.ownerPublicKey);
      personalIdDto.isCreatedByAssistant = true;
    }

    const personalId = await sjDb.collections.personalIdentifiersDocuments!.create(
      personalIdDto,
      publicKeys,
    );
    onCreate?.({ colName: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, id: personalId!.id });
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <FormProvider {...form}>
      <Form
        control={form.control}
        onSubmit={({ data }) => onSubmit(data)}
        render={({ submit }) => (
          <StyledDrawer width={400} footer={null} closable={false} onClose={handleClose} open>
            <Container>
              <Header>
                <BtnClose
                  onClick={handleClose}
                  style={{ position: 'absolute', right: 24, top: 24 }}
                />
                <StyledTitle className="truncate" size={22} bold>
                  {t('titleAddDocument')} - {values.type(personalIdType)}
                </StyledTitle>
              </Header>
              <Body>{Component}</Body>
              <Footer>
                <Button type="text" onClick={handleClose}>
                  {t('btnCancel')}
                </Button>
                <Button onClick={submit}>{t('btnSave')}</Button>
              </Footer>
            </Container>
          </StyledDrawer>
        )}
      />
    </FormProvider>
  );
};
